import React, { useState, useEffect, ChangeEvent } from "react";
import { Spinner } from "../index";
import { Button } from "@material-ui/core";
import { Pagination } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { PrimaryColor } from "../../shared/styles/colors";
import GalleryModel from "../../model/gallery";
import ApiService from "../../service/ApiService";
import PageResultResponse from "../../model/page-result-response";
import Service from "../../model/service";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import FullscreenImage from "../../shared/components/images/FullscreenImage";
import useStyles from "./styles";
import Misc from "../../utils/Misc";

export default function Gallery() {
  const classes = useStyles();

  const [items, setItems] = useState<GalleryModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service>();
  const [fullScreen, setFullScreen] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const serviceId = searchParams.get("serviceId");
    if (serviceId && !selectedService) {
      searchParams.set("serviceId", serviceId);
      const selectedService = services.find(
        (srv) => srv.id === parseInt(serviceId)
      );
      setSelectedService(selectedService);
    }

    searchParams.set("page", page.toString());
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (selectedService?.id) searchParams.set("serviceId", selectedService?.id?.toString());
      searchParams.set("page", page.toString());
      setSearchParams(searchParams);

      await ApiService.get<PageResultResponse>(
        `frontend/gallery?${searchParams.toString()}`
      )
        .then((data: PageResultResponse) => {
          if (Misc.isArrayEmpty(data.items)) return;
          const { items, pageCount } = data;
          setItems(items);
          setTotalPages(pageCount);
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false);
          }, 3000);
        });
    };

    const fetchServices = async () => {
      await ApiService.get<Service[]>("frontend/galleryServices").then(
        (data) => {
          setServices(data);
        }
      );
    };

    if (Misc.isArrayEmpty(services)) {
      fetchServices();
    }
    fetchData();
  }, [selectedService, page]);

  const handlePageChange = (event: ChangeEvent<unknown>, pg: number) => {
    setPage(pg);
    scrollToTop();
  };

  const handleServiceClick = (srvc: Service | undefined) => {
    if (!srvc) {
      searchParams.delete("serviceId");
    }

    setSearchParams(searchParams);
    setSelectedService(srvc);
    setPage(1);
    setItems([]);
    scrollToTop();
  };

  const toggleFullScreen = (url: string) => {
    setImageUrl(url);
    setFullScreen((fullScreen) => !fullScreen);
    // Prevent window from scrolling
    document.body.style.overflow = url !== "" ? "hidden" : "auto";
  };

  return (
    <>
      <Helmet>
        <title>Gallery - Setup Remodeling</title>
        <meta
          name="description"
          content="Check our Gallery to view the excellence in our work. Setup Remodeling"
        ></meta>
        <link rel="canonical" href="/gallery" />
      </Helmet>
      {fullScreen && (
        <FullscreenImage
          imageUrl={imageUrl}
          toggleFullScreen={toggleFullScreen}
        />
      )}
      <div className={classes.container}>
        <div className={classes.titleDiv}>
          <span className={classes.title}>Our Work</span>
          {Misc.isArrayEmpty(services) || loading ? (
            <Spinner />
          ) : (
            <>
              <div className={classes.filters}>
                <Button
                  className={
                    !selectedService?.id ? classes.buttonClicked : classes.button
                  }
                  disabled={!selectedService?.id}
                  onClick={() => handleServiceClick(undefined)}
                >
                  View All
                </Button>
                {services.map((service: Service) => (
                  <>
                    <span style={{ color: PrimaryColor }}>/</span>
                    <Button
                      key={service.id}
                      className={
                        service.id === selectedService?.id
                          ? classes.buttonClicked
                          : classes.button
                      }
                      disabled={service.id === selectedService?.id}
                      onClick={() => handleServiceClick(service)}
                    >
                      {service.name}
                    </Button>
                  </>
                ))}
              </div>
            </>
          )}
        </div>
        {Misc.isArrayEmpty(items) || loading ? (
          <>Gallery is loading</>
        ) : (
          <>
            <div className="d-flex row justify-content-center">
              <ImageList
                variant={window.screen.width < 640 ? "quilted" : "masonry"}
                cols={3}
                gap={8}
                sx={{
                  width: 1300,
                  maxWidth: 1536,
                  "@media (max-width: 640px)": { width: "100%" },
                }}
              >
                {items.map((item) => (
                  <ImageListItem
                    key={item.id}
                    cols={3}
                    sx={{ cursor: "zoom-in" }}
                  >
                    <img
                      srcSet={`${item}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                      src={`${item.imageUrl}`}
                      alt={item.description}
                      loading="lazy"
                      onClick={() => toggleFullScreen(item.imageUrl)}
                    />
                    <ImageListItemBar
                      title={item.description}
                      subtitle={`Service: ${item.Service?.name}`}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
              <div className={classes.pagination}>
                <Pagination
                  count={totalPages}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  page={page}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
